import React, { useEffect, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useScreenWidth from '~/hooks/useScreenWidth';
import { displayTooltip } from '~/hooks/displayTooltip';

//  Actions
import { success } from '~/redux/actions/snackbar-actions';

// Utils
import { copyToClipboard } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { stringLength } from '~/utils/helperFunctions';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

// Components
import TestWebhookModal from '~/components/Settings/Webhooks/TestWebhookModal';
import TruncatedText from '~/components/General/TruncatedText';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import { Box, Typography, Tooltip, Stack, IconButton } from '@mui/material';

// Icons
import {
  HideWebhookCIcon,
  ShowWebhookCIcon,
  CopyWebhookCIcon,
  EditWebhookCIcon,
  DisableCIcon,
  CheckmarkCIcon,
  DeleteWebhookCIcon,
  NoContactCIcon,
  SentCIcon,
  ResendCIcon,
  ModalTrashIcon,
} from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WebhookIcon from '@mui/icons-material/WebhookOutlined';

// Styles
import './styles.scss';

const WebhookListing = (props) => {
  const {
    setConfirmModal,
    updatePagination,
    pagination,
    initialLoad,
    handleDeleteWebhook,
    handleUpdateWebhookStatus,
  } = props;

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showSecrectKey, setSecretKey] = useState([]);
  const [testWebhookModal, setTestWebhookModal] = useState(false);
  const [selectedWebhookForTest, setSelectedWebhookForTest] = useState(null);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);

  const rows = useSelector((state) => state.webhooksReducers.webhooks);
  const rowsCount = useSelector((state) => state.webhooksReducers.rowsCount);
  const to = useSelector((state) => state.webhooksReducers.to);
  const from = useSelector((state) => state.webhooksReducers.from);
  const lastPage = useSelector((state) => state.webhooksReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.webhooksReducers.currentPage || 1);
  const perPage =
    useSelector((state) => state.webhooksReducers.perPage) || pagination?.pageSize || 10;
  const isLoading = useSelector((state) => state.webhooksReducers.loading);
  const pageSizes = DEFAULT_PAGE_SIZES;
  const paginationPageSizes = pageSizes.includes(perPage)
    ? pageSizes
    : [perPage, ...pageSizes].filter(Boolean);
  const showLoader = initialLoad || isLoading || paginationLoading;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggleSecretVisibility = (id, event) => {
    event?.stopPropagation();
    setSecretKey((visibleSecretIds) =>
      visibleSecretIds.includes(id)
        ? visibleSecretIds.filter((visibleSecretId) => visibleSecretId !== id)
        : [...visibleSecretIds, id]
    );
  };

  const handleDelete = (row, event) => {
    event.stopPropagation();
    setConfirmModal({
      open: true,
      message: MESSAGES.SETTINGS.WEBHOOKS.DELETE.HEADING,
      header: MESSAGES.SETTINGS.WEBHOOKS.DELETE.TITLE,
      icon: ModalTrashIcon,
      loading: false,
      handleOk: () => handleDeleteWebhook(row.id),
      handleClose: () =>
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
        })),
      successButtonName: MESSAGES.SETTINGS.WEBHOOKS.DELETE.SUBMIT_BUTTON,
      cancelButtonName: MESSAGES.SETTINGS.WEBHOOKS.DELETE.CANCEL_BUTTON,
    });
  };

  const handleUpdate = (row, event) => {
    event.stopPropagation();
    navigate(`/settings/webhooks/update/${row.id}`);
  };

  const handleDisable = (row, event) => {
    event.stopPropagation();
    const isDisabling = row.status === 'Enabled';
    setConfirmModal({
      open: true,
      message: isDisabling
        ? MESSAGES.SETTINGS.WEBHOOKS.STATUS.DISABLE_HEADING
        : MESSAGES.SETTINGS.WEBHOOKS.STATUS.ENABLE_HEADING,
      header: isDisabling
        ? MESSAGES.SETTINGS.WEBHOOKS.STATUS.DISABLE_TITLE
        : MESSAGES.SETTINGS.WEBHOOKS.STATUS.ENABLE_TITLE,
      component: <WebhookIcon />,
      containedIcon: true,
      loading: false,
      handleOk: () => handleUpdateWebhookStatus(row),
      handleClose: () =>
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
        })),
      successButtonName: MESSAGES.SETTINGS.WEBHOOKS.STATUS.SUBMIT_BUTTON,
      cancelButtonName: MESSAGES.SETTINGS.WEBHOOKS.STATUS.CANCEL_BUTTON,
    });
  };

  const handleTest = (row, event) => {
    event.stopPropagation();
    setSelectedWebhookForTest(row.id);
    setTestWebhookModal(true);
  };

  const copyKey = (key, event) => {
    event.stopPropagation();
    copyToClipboard(key);
    dispatch(success(MESSAGES.SETTINGS.API_KEYS.COPY_KEY));
  };

  const handleRowClick = (params, event) => {
    if (event.ctrlKey || event.metaKey) {
      window.open(`/settings/webhooks/${params.id}`, '_blank');
    } else {
      navigate(`/settings/webhooks/${params.id}`, {
        state: { webhook: params.row },
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    updatePagination({
      page: newPage,
      pageSize: perPage,
    });
  };

  const handlePageSizeChange = (event) => {
    setPaginationLoading(true);
    updatePagination({ page: 1, pageSize: event.target.value });
  };

  const isTooltip = displayTooltip(3200);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const open = Boolean(anchorEl);
  const isScreen = useScreenWidth();

  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveRowId(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setActiveRowId(null);
    }, 200);
  };

  const columns = [
    {
      field: 'callbackUrl',
      headerName: 'URL',
      width: 200,
      compactGrow: 3,
      compactMinWidth: 260,
      compactMaxWidth: 520,
      compactShrinkMinWidth: 160,
      compactShrinkPriority: 1,
      compactValueGetter: (row) => row.callbackUrl?.slice(0, 18) || ' -',
      flex: window.innerWidth > 1399 ? 1 : 0,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.callbackUrl || ' -'} />,
    },
    {
      field: 'topic',
      headerName: 'Description',
      width: 200,
      compactGrow: 2,
      compactMinWidth: 170,
      compactMaxWidth: 200,
      compactShrinkMinWidth: 125,
      compactShrinkPriority: 1,
      flex: window.innerWidth > 1399 ? 1 : 0,
      sortable: false,
      renderCell: (params) => <TruncatedText text={params.row.topic || ' -'} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'number',
      compactMinWidth: 100,
      compactMaxWidth: 140,
      compactShrinkMinWidth: 85,
      compactShrinkPriority: 1,
      flex: window.innerWidth > 1399 ? 1 : 0,
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.status || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'secret',
      headerName: 'Secret Key',
      width: 200,
      minWidth: 200,
      compactGrow: 3,
      compactMinWidth: 200,
      compactMaxWidth: 420,
      compactShrinkMinWidth: 140,
      compactShrinkPriority: 0,
      flex: window.innerWidth > 1399 ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="secretKey webhookSecretKey">
          <Tooltip title={showSecrectKey.includes(params.row.id) ? params.row.secret : ''}>
            <input
              type={showSecrectKey.includes(params.row.id) ? 'text' : 'password'}
              value={
                showSecrectKey.includes(params.row.id)
                  ? params.row.secret
                  : params.row.secret.slice(0, 13)
              }
              readOnly
            />
          </Tooltip>
          <Box className="hideShow">
            {showSecrectKey.includes(params.row.id) ? (
              <img
                src={HideWebhookCIcon}
                alt=""
                role="button"
                tabIndex={0}
                aria-label={`Hide secret for ${params.row.topic || params.row.callbackUrl}`}
                onClick={(event) => toggleSecretVisibility(params.row.id, event)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleSecretVisibility(params.row.id, event);
                  }
                }}
              />
            ) : (
              <img
                src={ShowWebhookCIcon}
                alt=""
                role="button"
                tabIndex={0}
                aria-label={`Show secret for ${params.row.topic || params.row.callbackUrl}`}
                onClick={(event) => toggleSecretVisibility(params.row.id, event)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleSecretVisibility(params.row.id, event);
                  }
                }}
              />
            )}
            <img
              src={CopyWebhookCIcon}
              alt=""
              role="button"
              tabIndex={0}
              aria-label={`Copy secret for ${params.row.topic || params.row.callbackUrl}`}
              onClick={(e) => copyKey(params.row.secret, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  copyKey(params.row.secret, e);
                }
              }}
            />
          </Box>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 180,
      compactMinWidth: 150,
      compactMaxWidth: 180,
      compactShrinkMinWidth: 130,
      compactShrinkPriority: 1,
      flex: window.innerWidth > 1399 ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.createdAt) || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'lastAccessAt',
      headerName: 'Last Used Date',
      width: 180,
      compactMinWidth: 150,
      compactMaxWidth: 180,
      compactShrinkMinWidth: 130,
      compactShrinkPriority: 1,
      flex: window.innerWidth > 1399 ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.lastAccessAt) || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const actionsAvailable = true; // Webhooks actions are always available
        const isMenuOpen = open && activeRowId?.id === params.row.id;

        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <Tooltip
              title={!actionsAvailable ? 'Actions not available' : ''}
              placement="left"
              disableInteractive
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -15],
                      },
                    },
                  ],
                },
              }}
            >
              <span>
                <IconButton
                  aria-label={`Open actions for webhook ${
                    params.row.topic || params.row.callbackUrl || params.row.id
                  }`}
                  id={`webhook-actions-${params.row.id}`}
                  aria-controls={isMenuOpen ? 'webhook-actions-menu' : undefined}
                  aria-expanded={isMenuOpen ? 'true' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => actionsAvailable && handleMenuClick(e, params.row)}
                  disabled={!actionsAvailable}
                  disableRipple
                  className="olcTableDropdownIcon"
                  sx={{
                    opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    setPaginationLoading(false); // Reset pagination loading when new data arrives
  }, [rows]);

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [window.innerWidth]);

  // Delayed "No Results" overlay, matching other tables
  useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && rows.length === 0) {
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, rows.length]);

  return (
    <>
      <Box
        className={`olcTableGridInternal ${initialLoad || isLoading || paginationLoading ? 'table-loading' : ''} ${rows.length === 0 ? 'table-empty' : ''}`}
      >
        <Box sx={{ position: 'relative' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row?.id}
            disableColumnMenu
            disableRowSelectionOnClick
            pagination={true}
            paginationMode="server"
            sx={{
              ...(showNoRows ||
              (!(initialLoad || isLoading || paginationLoading) && rows.length === 0)
                ? {
                    height: 'auto',
                    minHeight: '200px',
                  }
                : {}),
            }}
            rowHeight={61}
            shrinkColumnsToFit
            loading={initialLoad || isLoading || paginationLoading}
            rowSelection={false}
            onRowClick={handleRowClick}
            rowCount={rowsCount}
            hideFooterSelectedRowCount
            hideFooterPagination
            className="webhookListing"
            slots={{
              noRowsOverlay: () =>
                showNoRows ? (
                  <Typography className="noResultsTextWebhook">No Results Found</Typography>
                ) : null,
              noResultsOverlay: () =>
                showNoRows ? (
                  <Typography className="noResultsTextWebhook">No Results Found</Typography>
                ) : null,
            }}
          />
        </Box>

        {(showLoader || rowsCount > 0) && (
          <OlcTablePagination
            count={lastPage}
            page={currentPage}
            pageSize={perPage}
            pageSizes={paginationPageSizes}
            onPageChange={handleChangePage}
            onPageSizeChange={handlePageSizeChange}
            from={from}
            to={to}
            total={rowsCount}
            loading={showLoader}
          />
        )}
      </Box>

      <OlcActionMenu
        id="webhook-actions-menu"
        labelledBy={activeRowId ? `webhook-actions-${activeRowId.id}` : undefined}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        actions={[
          {
            label: 'Test',
            icon: SentCIcon,
            onClick: (event) => handleTest(activeRowId, event),
          },
          {
            label: activeRowId?.status === 'Enabled' ? 'Disable' : 'Enable',
            icon: activeRowId?.status === 'Enabled' ? DisableCIcon : CheckmarkCIcon,
            onClick: (event) => handleDisable(activeRowId, event),
          },
          {
            label: 'Edit',
            icon: EditWebhookCIcon,
            onClick: (event) => handleUpdate(activeRowId, event),
          },
          {
            label: 'Delete',
            icon: DeleteWebhookCIcon,
            onClick: (event) => handleDelete(activeRowId, event),
          },
        ]}
      />

      <TestWebhookModal
        openModal={testWebhookModal}
        handleCloseModal={() => setTestWebhookModal(false)}
        webhookId={selectedWebhookForTest}
      />
    </>
  );
};

export default WebhookListing;
